<template>
  <TargetCell />
</template>

<script setup>
import { isNumber } from 'lodash'
import { computed, h, getCurrentInstance } from 'vue'

import { currentTargetVisibility } from '@/utils/cell-helper'
import { DATES_RANGE_SEPARATOR } from '@/utils/date'
import { resolveProgressValue } from '@/utils/element-progress'
import { isJiraIssueOrWorkItem, isKR } from '@/utils/objective-types'
import {
  getThresholdConditionSymbol,
  isThresholdProgress,
  METRIC_VALUES_KEYS
} from '@/utils/okr-element-values'
import { getScopeId } from '@/utils/render-function-helpers'
import { uid } from '@/utils/uid'

import CellValue from '@/components/objectives/table/cells/CellValue'
import AppCipheredText from '@/components/ui/AppCipheredText'

defineOptions({
  name: 'CurrentTargetCell'
})

const props = defineProps({
  item: {
    type: Object,
    required: true
  },

  readable: {
    type: Boolean
  }
})

const isKr = computed(() => isKR(props.item))
const isTask = computed(() => isJiraIssueOrWorkItem(props.item))
const isVisibleValue = computed(() => currentTargetVisibility(props.item))
const isThreshold = computed(() => {
  return (
    (isKr.value || isTask.value) &&
    isThresholdProgress({
      element: props.item
    })
  )
})
const id = `st${uid()}`

const TargetCell = () => {
  if (props.item.isNestedTask) {
    return null
  }

  const scopeId = getScopeId(getCurrentInstance())

  if ((isKr.value || isTask.value) && isVisibleValue.value) {
    return h(
      CellValue,
      {
        id,
        cellValue: '',
        userHasUpdateAccess: true,
        userHasReadAccess: props.readable
      },
      // arrow function is fix for "Non-function value encountered for default slot. Prefer function slots for better performance." warning
      () => {
        if (!props.readable) {
          return h(AppCipheredText, {
            style: {
              fontSize: '14px'
            }
          })
        }

        let normalizedCurrentValue =
          props.item[METRIC_VALUES_KEYS.CURRENT_VALUE] ?? DATES_RANGE_SEPARATOR

        if (isNumber(normalizedCurrentValue)) {
          normalizedCurrentValue = resolveProgressValue({
            value: normalizedCurrentValue,
            maxValue: 1e9
          })
        }

        const thresholdSymbol = isThreshold.value
          ? `${getThresholdConditionSymbol({
              conditionId: props.item[METRIC_VALUES_KEYS.THRESHOLD_CONDITION_TYPE_ID]
            })}`
          : ''

        const targetPart = ['/', thresholdSymbol, `${props.item.activeTarget}`]
          .filter(Boolean)
          .join(' ')

        return [
          isVisibleValue.value
            ? [
                h(
                  'span',
                  {
                    ...scopeId,
                    class: 'ctc-CurrentValue'
                  },
                  normalizedCurrentValue
                ),
                ' ', // space between values
                h('span', { ...scopeId, class: 'ctc-TargetValue' }, `${targetPart}`)
              ]
            : null
        ]
      }
    )
  }
}
</script>

<style lang="scss" scoped>
/* eslint-disable vue-scoped-css/no-unused-selector */

.ctc-CurrentValue {
  font-family: $system-ui;
  font-style: normal;
  font-weight: fw('semi-bold');
  font-size: $fs-14;
  line-height: 20px;
  color: $dark-1;
}
.ctc-TargetValue {
  font-family: $system-ui;
  font-weight: fw('regular');
  color: $dark-3;
}

/* eslint-enable vue-scoped-css/no-unused-selector */
</style>
